import { useQuery } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTablePFSEvent } from "primereact/datatable";
import { Dropdown, DropdownChangeParams } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { If } from "../../../components/If/If";
import { LabelValue } from "../../../components/LabelValue/LabelValue";
import { Loader } from "../../../components/Loader/Loader";
import { SectionHeader } from "../../../components/SectionHeader/SectionHeader";
import { debounce } from "../../../helpers/debounce";
import { useFilterQuery } from "../../../hooks/useFilterQuery";
import { requests } from "../../../services/http-common";
import { IFilter } from "../../../types";

import styles from "./EmployeeList.module.scss";
import { renderDateColumn } from "../../../helpers/renderDateColumn";
import { Actions } from "./columns/employee-actions";
import { EmployeeStatus } from "./columns/EmployeeStatus";
import {
  EmployeeRoles,
  EmployeeRolesMapping,
  EmployeeStatuses,
} from "../../../models/Employee";
import { ImportExportData } from "../../../components/import-export-data/import-export-data";
import { PrivateAccess } from "../../../keycloak";
import { UserRoles } from "../../../constants/userRoles";
import { useEffectWithoutMount } from "../../../helpers/helperHooks";

const statusOptions = [
  {
    label: "All",
    value: "",
  },
  {
    label: "Active",
    value: EmployeeStatuses.ACTIVE,
  },
  // {
  //   label: "Terminated",
  //   value: EmployeeStatuses.TERMINATED,
  // },
  {
    // label: "Terminated(Rehirable)",
    label: "Terminated",
    value: EmployeeStatuses.TERMINATED_REHIRABLE,
  },
];

const defaultFilters = {
  first: 0,
  rows: 10,
  page: 1,
  filter: "",
  filterByStatus: "",
  filterByCustomerCategory: "",
  filterByProjectType: "",
  sortOrder: -1,
};

export const EmployeeList = () => {
  const [filters, setFilters] = useState<IFilter>(defaultFilters as IFilter);
  const [term, setTerm] = useState("");
  const filterQuery = useFilterQuery(filters);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: [`employee-list`],
    queryFn: () => {
      return requests.get(`/user?${filterQuery}`);
    },
    retry: false,
  });

  useEffectWithoutMount(() => {
    refetch();
  }, [filters, refetch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnSearch = useCallback(
    debounce((event: any) => {
      setFilters((prev) => ({ ...prev, filter: event.target.value }));
    }, 500),
    []
  );

  const handleInputSearch = (e: any) => {
    debouncedOnSearch(e);
    setTerm(e.target.value);
  };

  const onDropdownChange = (e: DropdownChangeParams) => {
    setFilters((prev) => ({ ...prev, [e.target.id]: e.value }));
  };

  const onPage = useCallback(
    (event: DataTablePFSEvent) => {
      setFilters((currVal: any) => ({
        ...currVal,
        first: event.first,
        rows: event.rows,
        page: event.page,
      }));
    },
    [setFilters]
  );

  return (
    <div className={styles.container}>
      <SectionHeader title="Employees" />
      <PrivateAccess
        roles={{
          dcs_client: [UserRoles.USER_CREATE],
        }}
      >
        <Link to="create" className={styles.createBtn}>
          <Button
            label="Create Employee"
            aria-label="create-employee"
            className="p-button-sm"
          />
        </Link>
      </PrivateAccess>
      <div className={styles.filters}>
        <LabelValue label="Select Status">
          <Dropdown
            appendTo="self"
            options={statusOptions}
            id="filterByStatus"
            onChange={onDropdownChange}
            value={filters.filterByStatus}
            className={styles.dropdown}
          />
        </LabelValue>
        <div>
          <ImportExportData
            exportUrl={`/user/export-csv?${filterQuery}`}
            importUrl="/user/import-csv"
            refetchQury={refetch}
          />
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              onChange={handleInputSearch}
              value={term}
              placeholder="Search"
            />
          </span>
        </div>
      </div>
      <If condition={isLoading}>
        <Loader />
        <div className={styles.table}>
          <DataTable
            value={data?.result}
            paginator={true}
            paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
            first={filters.first}
            rows={filters.rows}
            totalRecords={data?.total}
            onPage={onPage}
            paginatorPosition="top"
            responsiveLayout="scroll"
            paginatorClassName={styles.paginator}
            lazy
          >
            <Column
              field="role"
              header="Position"
              body={(rowData) =>
                EmployeeRolesMapping[rowData?.role as EmployeeRoles]
              }
            />
            <Column field="firstName" header="Name" />
            <Column field="lastName" header="Surname" />
            <Column field="email" header="Email Address" />
            <Column
              field="hireDate"
              header="Hiring Date"
              body={(rowData) => renderDateColumn(rowData.hireDate)}
            />
            <Column
              field="terminationDate"
              header="Termination Date"
              body={(rowData) => renderDateColumn(rowData.terminationDate)}
            />
            <Column
              field="status"
              header="Status"
              body={(rowData: {
                status: EmployeeStatuses;
                id: string;
                role: EmployeeRoles;
              }) => (
                <PrivateAccess
                  roles={{
                    dcs_client: [UserRoles.USER_VIEW],
                  }}
                >
                  <EmployeeStatus {...rowData} />
                </PrivateAccess>
              )}
            ></Column>
            <Column
              header="Actions"
              body={(rowData: {
                id: string;
                isDisabled: boolean;
                status: EmployeeStatuses;
              }) => <Actions {...rowData} isDisabled={isFetching} />}
            />
          </DataTable>
        </div>
      </If>
    </div>
  );
};
